import React from 'react';
import {useFormContext} from 'react-hook-form';
import {defineMessages, FormattedNumber} from 'react-intl';
import {ErrorAlert, InfoAlert} from '@igp/shared/components/alerts';
import {Button} from '@igp/shared/components/button';
import {LocalizedText} from '@igp/shared/components/i18n';
import {KeyValueCell, KeyValueColumn, KeyValueTable} from '@igp/shared/components/key-value-table';
import {BulkModalTitle, ModalFooter} from '@igp/shared/components/modal';
import {formatTimestamp} from '@igp/shared/utils';
import {Divider} from '@mui/material';
import Typography from '@mui/material/Typography';
import {makeStyles} from 'tss-react/mui';

import type {AdjustDebtFormModel} from '../types';

const localized = defineMessages({
    title: {
        id: 'AdjustDebtConfirmation_title',
        defaultMessage: 'Confirm Debt Adjustment',
    },
    infoAlert: {
        id: 'AdjustDebtConfirmation_infoAlert',
        defaultMessage: 'Please confirm carefully. This action is permanent.',
    },
    errorAlert: {
        id: 'AdjustDebtCOnfirmation_errorAlert',
        defaultMessage: 'Adjustment failed. Please try again.',
    },
    back: {
        id: 'AdjustDebtConfirmation_back',
        defaultMessage: 'Back',
    },
    confirm: {
        id: 'AdjustDebtConfirmation_confirm',
        defaultMessage: 'Confirm',
    },
    agentId: {
        id: 'AdjustDebtConfirmation_agentId',
        defaultMessage: 'Agent ID',
    },
    username: {
        id: 'AdjustDebtConfirmation_username',
        defaultMessage: 'Username',
    },
    currentDebt: {
        id: 'AdjustDebtForm_currentDebt',
        defaultMessage: 'Current Debt',
    },
    adjustment: {
        id: 'AdjustDebtForm_adjustment',
        defaultMessage: 'Adjustment',
    },
    debtAfter: {
        id: 'AdjustDebtForm_debtAfter',
        defaultMessage: 'Debt After',
    },
    Add: {
        id: 'AdjustDebtForm_Add',
        defaultMessage: 'Add',
    },
    Deduct: {
        id: 'AdjustDebtForm_Deduct',
        defaultMessage: 'Deduct',
    },
    transferDate: {
        id: 'AdjustmentDebtForm_transferDate',
        defaultMessage: 'Adjustment Date',
    },
});
const useStyles = makeStyles()(theme => ({
    divider: {
        borderColor: theme.palette.common.black,
    },
}));

type AdjustDebtConfirmationProps = {
    onSubmit: () => void;
    onBack: () => void;
    isProgress: boolean;
    isError: boolean;
};

export function AdjustDebtConfiramtion({onSubmit, onBack, isProgress, isError}: AdjustDebtConfirmationProps) {
    const {classes} = useStyles();
    const {getValues} = useFormContext<AdjustDebtFormModel>();
    const values = getValues();
    const resultDebt =
        values.adjustType === 'Add' ? values.currentDebt + Number(values.amount) : values.currentDebt - Number(values.amount);
    return (
        <>
            <BulkModalTitle title={localized.title} />
            {isError ? <ErrorAlert title={localized.errorAlert} /> : <InfoAlert title={localized.infoAlert} />}
            <KeyValueTable>
                <KeyValueColumn>
                    <KeyValueCell title={<LocalizedText label={localized.agentId} />}>
                        <Typography variant="body1">{values.agentUid}</Typography>
                    </KeyValueCell>
                    <KeyValueCell title={<LocalizedText label={localized.username} />}>
                        <Typography variant="body1">{values.username}</Typography>
                    </KeyValueCell>
                    <KeyValueCell title={<LocalizedText label={localized.currentDebt} />}>
                        <Typography variant="body1">
                            <FormattedNumber value={values.currentDebt} format="USD" />
                        </Typography>
                    </KeyValueCell>
                    <KeyValueCell title={<LocalizedText label={localized.transferDate} />}>
                        <Typography variant="body1">{formatTimestamp(values.transferDate, 'date', false)}</Typography>
                    </KeyValueCell>
                    <KeyValueCell title={<LocalizedText label={localized.adjustment} />}>
                        <Typography variant="body1" color={values.adjustType === 'Add' ? 'error' : 'success'}>
                            <LocalizedText label={localized[values.adjustType]} />{' '}
                            <FormattedNumber value={Number(values.amount)} format="USD" />
                        </Typography>
                    </KeyValueCell>
                </KeyValueColumn>
            </KeyValueTable>
            <Divider className={classes.divider} />
            <KeyValueTable>
                <KeyValueColumn>
                    <KeyValueCell titleVariant="body1" title={<LocalizedText label={localized.debtAfter} />}>
                        <Typography variant="body1">
                            <FormattedNumber value={resultDebt} format="USD" />
                        </Typography>
                    </KeyValueCell>
                </KeyValueColumn>
            </KeyValueTable>
            <ModalFooter>
                <Button label={localized.back} onClick={onBack} />
                <Button label={localized.confirm} onClick={onSubmit} color="primary" disabled={isProgress} />
            </ModalFooter>
        </>
    );
}
